/* @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import type { CSSProperties } from '@coursera/coursera-ui';
import { breakPoint } from '@coursera/coursera-ui';

import {
  getCurrentPaymentPassOption,
  getSortedPaymentPassOptions,
  useNonRecurringChoiceData,
} from 'bundles/enroll-modal/components/s12n-non-recurring/utils/nonRecurringChoiceDataUtils';
import Icon from 'bundles/iconfont/Icon';
import type { PrepaidProduct } from 'bundles/ownable-products-common/types/ProductInfoTypes';
import TrackedButton from 'bundles/page/components/TrackedButton';

import _t from 'i18n!nls/s12n-enroll';

export type PropsFromCaller = {
  choiceType: string;
  onButtonClick: (paymentPassOption: PrepaidProduct) => void;
  isEnrolling: boolean;
  auditComponent?: JSX.Element;
};

const useStyles = (): Record<string, CSSProperties> => {
  return {
    nonRecurringFooter: {
      width: '100%',
      background: 'var(--cds-color-neutral-background-primary)',
      padding: '16px 50px',
      boxShadow: '0px -4px 4px var(--cds-color-darken-100)',
      [`@media (max-width: ${breakPoint.md - 1}px)`]: {
        padding: '16px 30px',
      },
    },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'center',
    },
    continueButton: {
      background: 'var(--cds-color-blue-700)',
      color: 'var(--cds-color-neutral-primary-invert)',
      border: 'none',
      borderRadius: '2px',
      padding: '16px 40px',
      fontWeight: 700,
      fontSize: '14px',
      width: '100%',
      maxWidth: '300px',
      '&:disabled': {
        background: 'var(--cds-color-grey-25)',
        borderColor: 'var(--cds-color-grey-25)',
        color: 'var(--cds-color-darken-300)',
      },
    },
  };
};

const S12nNonRecurringChoiceFooter = ({ choiceType, onButtonClick, isEnrolling, auditComponent }: PropsFromCaller) => {
  const styles = useStyles();
  const { prepaidOptions } = useNonRecurringChoiceData();

  if (!prepaidOptions) {
    return null;
  }

  const sortedPaymentPassOptions = getSortedPaymentPassOptions(prepaidOptions);
  const paymentPassOption = getCurrentPaymentPassOption(choiceType, sortedPaymentPassOptions);

  return (
    <div css={styles.nonRecurringFooter}>
      <div css={styles.buttonContainer}>
        <TrackedButton
          data-test="continueButton"
          trackingName="continue_button"
          className="cozy"
          css={styles.continueButton}
          // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
          onClick={() => onButtonClick(paymentPassOption)}
          disabled={isEnrolling}
        >
          {isEnrolling ? <Icon name="spinner" spin /> : _t('Continue')}
        </TrackedButton>
      </div>
      {auditComponent}
    </div>
  );
};

export default S12nNonRecurringChoiceFooter;
